import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, Typography, Grid, Button, Chip, Avatar,
  Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Popover, TextField, Stack,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import OnlinePredictionIcon from '@mui/icons-material/OnlinePrediction';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupsIcon from '@mui/icons-material/Groups';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';

// ---------- shared helpers ----------
const apiUrl = 'https://ckwin44.com';

const fmtMoney = (n) => `৳ ${Number(n || 0).toLocaleString()}`;
const fmtNum = (n) => Number(n || 0).toLocaleString();

const fmtDate = (d) =>
  d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const toISO = (d) => d.toISOString().slice(0, 10);

const calcTrend = (current, previous) => {
  if (!previous) return { text: '0%', up: true };
  const change = ((current - previous) / previous) * 100;
  return { text: `${Math.abs(change).toFixed(1)}%`, up: change >= 0 };
};

async function fetchJSON(url) {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`${url} failed: ${res.status}`);
  return res.json();
}

// ---------- small building blocks ----------
const Panel = ({ title, action, children, sx }) => (
  <Card sx={{ p: 2, backgroundColor: '#1E293B', borderRadius: 2, height: '100%', ...sx }}>
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
      <Typography variant="subtitle1" fontWeight="bold" color="white">{title}</Typography>
      {action}
    </Box>
    {children}
  </Card>
);

const StatCard = ({ icon, iconBg, title, value, subtitle, trend }) => (
  <Card sx={{ p: 2, backgroundColor: '#1E293B', borderRadius: 2, height: '100%' }}>
    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
      <Box sx={{
        backgroundColor: iconBg, borderRadius: '50%', width: 36, height: 36,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Typography variant="body2" color="grey.400" noWrap>{title}</Typography>
    </Box>
    <Typography variant="h5" fontWeight="bold" color="white" noWrap>{value}</Typography>
    <Box display="flex" alignItems="center" gap={0.5} mt={1} flexWrap="wrap">
      {trend && (trend.up
        ? <ArrowUpwardIcon sx={{ fontSize: 14, color: '#22C55E' }} />
        : <ArrowDownwardIcon sx={{ fontSize: 14, color: '#EF4444' }} />)}
      {trend && (
        <Typography variant="caption" sx={{ color: trend.up ? '#22C55E' : '#EF4444' }}>
          {trend.text}
        </Typography>
      )}
      <Typography variant="caption" color="grey.500" ml={trend ? 1 : 0}>{subtitle}</Typography>
    </Box>
  </Card>
);

const StatRow = ({ icon, color, label, value, valueColor = 'white' }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between" py={0.75} gap={1}>
    <Box display="flex" alignItems="center" gap={1} minWidth={0}>
      <Box sx={{ color, display: 'flex', flexShrink: 0 }}>{icon}</Box>
      <Typography variant="body2" color="grey.400" noWrap>{label}</Typography>
    </Box>
    <Typography variant="body2" fontWeight={600} color={valueColor} noWrap>{value}</Typography>
  </Box>
);

const AlertRow = ({ icon, color, title, subtitle, count }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between" py={1} gap={1}>
    <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
      <Box sx={{
        backgroundColor: `${color}22`, color, borderRadius: '50%', width: 32, height: 32,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Box minWidth={0}>
        <Typography variant="body2" color="white" noWrap>{title}</Typography>
        <Typography variant="caption" color="grey.500" noWrap component="div">{subtitle}</Typography>
      </Box>
    </Box>
    <Chip label={count} size="small" sx={{ backgroundColor: `${color}22`, color, fontWeight: 700, flexShrink: 0 }} />
  </Box>
);

// ---------- date-range filter bar ----------
const RANGE_OPTIONS = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7D' },
  { key: '30d', label: '30D' },
  { key: 'custom', label: 'Custom' },
];

const RangeFilterBar = ({ range, setRange, customRange, setCustomRange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [draft, setDraft] = useState(customRange);
  const open = Boolean(anchorEl);

  const handleClick = (key, e) => {
    if (key === 'custom') {
      setDraft(customRange);
      setAnchorEl(e.currentTarget);
      return;
    }
    setRange(key);
  };

  const applyCustom = () => {
    if (!draft.start || !draft.end) return;
    setCustomRange(draft);
    setRange('custom');
    setAnchorEl(null);
  };

  const todayLabel = `Today (${fmtDate(new Date())})`;
  const customLabel = range === 'custom' && customRange.start && customRange.end
    ? `${customRange.start} → ${customRange.end}`
    : 'Custom';

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          backgroundColor: '#1E293B',
          borderRadius: 2,
          p: 0.5,
          overflowX: { xs: 'auto', sm: 'visible' },
          width: { xs: '100%', sm: 'auto' },
        }}
      >
        {RANGE_OPTIONS.map((opt) => {
          const active = range === opt.key;
          const isToday = opt.key === 'today';
          const isCustom = opt.key === 'custom';
          return (
            <Button
              key={opt.key}
              onClick={(e) => handleClick(opt.key, e)}
              startIcon={isToday ? <CalendarTodayIcon sx={{ fontSize: 16 }} /> : undefined}
              sx={{
                textTransform: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                borderRadius: 1.5,
                px: 2,
                color: active ? 'white' : 'grey.400',
                backgroundColor: active ? '#3B82F6' : 'transparent',
                '&:hover': { backgroundColor: active ? '#3B82F6' : '#334155' },
              }}
            >
              {isToday ? todayLabel : isCustom ? customLabel : opt.label}
            </Button>
          );
        })}
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { backgroundColor: '#1E293B', p: 2, border: '1px solid #334155' } } }}
      >
        <Stack spacing={2} sx={{ width: 260 }}>
          <Typography variant="subtitle2" color="white">Select custom range</Typography>
          <TextField
            type="date"
            label="Start date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={draft.start || ''}
            onChange={(e) => setDraft((d) => ({ ...d, start: e.target.value }))}
            sx={{
              input: { color: 'white' }, label: { color: 'grey.500' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' },
            }}
          />
          <TextField
            type="date"
            label="End date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={draft.end || ''}
            onChange={(e) => setDraft((d) => ({ ...d, end: e.target.value }))}
            sx={{
              input: { color: 'white' }, label: { color: 'grey.500' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' },
            }}
          />
          <Button
            variant="contained"
            disabled={!draft.start || !draft.end}
            onClick={applyCustom}
            sx={{ backgroundColor: '#3B82F6', textTransform: 'none' }}
          >
            Apply
          </Button>
        </Stack>
      </Popover>
    </>
  );
};

// ---------- main component ----------
export const DashboardOverview = () => {
  const [range, setRange] = useState('today');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const buildQuery = useCallback(() => {
    if (range === 'custom' && customRange.start && customRange.end) {
      return `range=custom&start=${customRange.start}&end=${customRange.end}`;
    }
    return `range=${range}`;
  }, [range, customRange]);

  const load = useCallback(() => {
    fetchJSON(`${apiUrl}/api/dashboard/overview/?${buildQuery()}`)
      .then((d) => { setData(d); setError(null); })
      .catch((err) => {
        console.error('Dashboard load failed:', err);
        setError('Dashboard data লোড করা যায়নি। আবার চেষ্টা করুন।');
      });
  }, [buildQuery]);

  useEffect(() => {
    load();
    if (pollRef.current) clearInterval(pollRef.current);
    // only auto-refresh on live ranges, not a fixed custom window
    if (range !== 'custom') {
      pollRef.current = setInterval(load, 30000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load, range]);

  if (error) {
    return <Typography color="error.light">{error}</Typography>;
  }
  if (!data) {
    return (
      <Box display="flex" alignItems="center" gap={1} color="white" p={4}>
        <CircularProgress size={20} color="inherit" /> Loading dashboard...
      </Box>
    );
  }

  const { stats, chart, action_required: ar, user_overview: uo, betting_overview: bo,
    agent_overview: ao, risk_alerts: alerts, support_overview: so,
    recent_transactions: txns, live_activity: activity } = data;

  const chartData = chart.labels.map((label, i) => ({
    label,
    Deposit: chart.deposit[i],
    Withdrawal: chart.withdrawal[i],
    Turnover: chart.turnover[i],
    'Net Revenue': chart.net_revenue[i],
    Bonus: chart.bonus[i],
  }));

  const growthData = uo.growth_chart.labels.map((label, i) => ({
    label,
    'New Users': uo.growth_chart.new_users[i],
    'Active Users': uo.growth_chart.active_users[i],
    Returning: uo.growth_chart.returning_users[i],
  }));

  // last-5 limits for the three lists
  const recentTxns = [...txns].slice(0, 5);
  const recentActivity = [...activity].slice(0, 5);
  const topAgents = [...ao.top_agents].slice(0, 5);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      {/* header row */}
      <Box
        display="flex"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        mb={3}
        flexWrap="wrap"
        gap={2}
        flexDirection={{ xs: 'column', sm: 'row' }}
      >
        <Box>
          <Typography variant="h5" fontWeight="bold" color="white" mb={0.5}>Dashboard Overview</Typography>
          <Typography variant="body2" color="grey.500">Welcome back, Admin 👋</Typography>
        </Box>
        <RangeFilterBar
          range={range}
          setRange={setRange}
          customRange={customRange}
          setCustomRange={setCustomRange}
        />
      </Box>

      {/* top stat cards */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={6} sm={6} md={4} lg={2}>
          <StatCard icon={<PeopleIcon sx={{ color: 'white', fontSize: 18 }} />} iconBg="#3B82F6"
            title="Total Users" value={fmtNum(stats.total_users)}
            trend={calcTrend(stats.new_users_today, stats.new_users_yesterday)}
            subtitle={`New Today: ${stats.new_users_today}`} />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={2}>
          <StatCard icon={<AccountBalanceWalletIcon sx={{ color: 'white', fontSize: 18 }} />} iconBg="#22C55E"
            title="Total Deposit" value={fmtMoney(stats.total_deposit)}
            trend={calcTrend(stats.total_deposit, stats.deposit_yesterday)}
            subtitle={`Yesterday: ${fmtMoney(stats.deposit_yesterday)}`} />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={2}>
          <StatCard icon={<MonetizationOnIcon sx={{ color: 'white', fontSize: 18 }} />} iconBg="#EF4444"
            title="Total Withdrawal" value={fmtMoney(stats.total_withdrawal)}
            trend={calcTrend(stats.total_withdrawal, stats.withdrawal_yesterday)}
            subtitle={`Yesterday: ${fmtMoney(stats.withdrawal_yesterday)}`} />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={2}>
          <StatCard icon={<TrendingUpIcon sx={{ color: 'white', fontSize: 18 }} />} iconBg="#A855F7"
            title="Total Turnover" value={fmtMoney(stats.total_turnover)}
            trend={calcTrend(stats.total_turnover, stats.turnover_yesterday)}
            subtitle={`Yesterday: ${fmtMoney(stats.turnover_yesterday)}`} />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={2}>
          <StatCard icon={<MonetizationOnIcon sx={{ color: 'white', fontSize: 18 }} />} iconBg="#14B8A6"
            title="Net Revenue (GGR)" value={fmtMoney(stats.net_revenue)}
            trend={calcTrend(stats.net_revenue, stats.net_revenue_yesterday)}
            subtitle={`Yesterday: ${fmtMoney(stats.net_revenue_yesterday)}`} />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={2}>
          <StatCard icon={<OnlinePredictionIcon sx={{ color: 'white', fontSize: 18 }} />} iconBg="#0EA5E9"
            title="Online Users" value={fmtNum(stats.online_users)}
            subtitle={`Total Active: ${fmtNum(stats.total_active_users)}`} />
        </Grid>
      </Grid>

      {/* chart + action required / quick actions */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} lg={8}>
          <Panel title="Revenue & Transaction Overview" sx={{ minHeight: 340 }}>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="label" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip contentStyle={{ backgroundColor: '#0F172A', border: '1px solid #334155' }} />
                <Legend />
                <Area type="monotone" dataKey="Deposit" stroke="#22C55E" fill="#22C55E33" />
                <Area type="monotone" dataKey="Withdrawal" stroke="#EF4444" fill="#EF444433" />
                <Area type="monotone" dataKey="Turnover" stroke="#3B82F6" fill="#3B82F633" />
                <Area type="monotone" dataKey="Net Revenue" stroke="#A855F7" fill="#A855F733" />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Box display="flex" flexDirection="column" gap={2} height="100%">
            <Panel title="Action Required">
              <AlertRow icon={<AccountBalanceWalletIcon fontSize="small" />} color="#F59E0B"
                title="Pending Deposits" subtitle={fmtMoney(ar.pending_deposits.amount)}
                count={ar.pending_deposits.count} />
              <AlertRow icon={<MonetizationOnIcon fontSize="small" />} color="#EF4444"
                title="Pending Withdrawals" subtitle={fmtMoney(ar.pending_withdrawals.amount)}
                count={ar.pending_withdrawals.count} />
              <AlertRow icon={<PersonAddIcon fontSize="small" />} color="#3B82F6"
                title="Pending Verification" subtitle={`New Users: ${ar.pending_verification.new_users_today}`}
                count={ar.pending_verification.count} />
              <AlertRow icon={<SupportAgentIcon fontSize="small" />} color="#0EA5E9"
                title="Open Support Tickets" subtitle={`High Priority: ${ar.open_support_tickets.high_priority}`}
                count={ar.open_support_tickets.count} />
            </Panel>

            <Panel title="Quick Actions">
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Button fullWidth size="small" startIcon={<PersonAddIcon />}
                    sx={{ backgroundColor: '#3B82F6', color: 'white', textTransform: 'none' }}>
                    Add User
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button fullWidth size="small" startIcon={<GroupsIcon />}
                    sx={{ backgroundColor: '#22C55E', color: 'white', textTransform: 'none' }}>
                    Manage Agents
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button fullWidth size="small" startIcon={<ReceiptLongIcon />}
                    sx={{ backgroundColor: '#1E293B', border: '1px solid #334155', color: 'white', textTransform: 'none' }}>
                    View Transactions
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button fullWidth size="small" startIcon={<SettingsIcon />}
                    sx={{ backgroundColor: '#1E293B', border: '1px solid #334155', color: 'white', textTransform: 'none' }}>
                    System Settings
                  </Button>
                </Grid>
              </Grid>
            </Panel>
          </Box>
        </Grid>
      </Grid>

      {/* user overview + betting overview */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={6}>
          <Panel title="User Overview">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <StatRow icon={<PeopleIcon fontSize="small" />} color="#3B82F6" label="Total Users" value={fmtNum(uo.total_users)} />
                <StatRow icon={<PersonAddIcon fontSize="small" />} color="#22C55E" label="New Users (Today)" value={fmtNum(uo.new_users_today)} />
                <StatRow icon={<PeopleIcon fontSize="small" />} color="#A855F7" label="Active Users" value={fmtNum(uo.active_users)} />
                <StatRow icon={<OnlinePredictionIcon fontSize="small" />} color="#22C55E" label="Online Now" value={fmtNum(uo.online_now)} valueColor="#22C55E" />
                <StatRow icon={<WarningAmberIcon fontSize="small" />} color="#EF4444" label="Blocked Users" value={fmtNum(uo.blocked_users)} />
                <StatRow icon={<PersonAddIcon fontSize="small" />} color="#F59E0B" label="Pending Verification" value={fmtNum(uo.pending_verification)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="grey.500">User Growth</Typography>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={growthData}>
                    <XAxis dataKey="label" stroke="#64748B" fontSize={10} />
                    <YAxis stroke="#64748B" fontSize={10} />
                    <Tooltip contentStyle={{ backgroundColor: '#0F172A', border: '1px solid #334155' }} />
                    <Line type="monotone" dataKey="New Users" stroke="#3B82F6" dot={false} />
                    <Line type="monotone" dataKey="Active Users" stroke="#22C55E" dot={false} />
                    <Line type="monotone" dataKey="Returning" stroke="#A855F7" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          </Panel>
        </Grid>

        <Grid item xs={12} md={6}>
          <Panel title="Betting Overview">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <StatRow icon={<TrendingUpIcon fontSize="small" />} color="#3B82F6" label="Total Bets" value={fmtNum(bo.total_bets)} />
                <StatRow icon={<MonetizationOnIcon fontSize="small" />} color="#A855F7" label="Total Turnover" value={fmtMoney(bo.total_turnover)} />
                <StatRow icon={<ArrowUpwardIcon fontSize="small" />} color="#22C55E" label="Winning Bets" value={`${fmtNum(bo.winning_bets.count)} (${bo.winning_bets.pct}%)`} />
                <StatRow icon={<ArrowDownwardIcon fontSize="small" />} color="#EF4444" label="Losing Bets" value={`${fmtNum(bo.losing_bets.count)} (${bo.losing_bets.pct}%)`} />
                <StatRow icon={<TrendingUpIcon fontSize="small" />} color="#F59E0B" label="Open Bets" value={`${fmtNum(bo.open_bets.count)} (${bo.open_bets.pct}%)`} />
                <StatRow icon={<WarningAmberIcon fontSize="small" />} color="grey.500" label="Cancelled Bets" value={`${fmtNum(bo.cancelled_bets.count)} (${bo.cancelled_bets.pct}%)`} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="grey.500">Top Games / Sports</Typography>
                {bo.top_games.map((g, i) => (
                  <Box key={g.name} display="flex" justifyContent="space-between" py={0.75} gap={1}>
                    <Typography variant="body2" color="grey.300" noWrap>{i + 1}. {g.name}</Typography>
                    <Typography variant="body2" color="white" fontWeight={600} noWrap>{fmtMoney(g.turnover)}</Typography>
                  </Box>
                ))}
              </Grid>
            </Grid>
          </Panel>
        </Grid>
      </Grid>

      {/* agent overview + risk alerts + support overview */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} lg={5}>
          <Panel title="Agent Overview">
            <Grid container spacing={2} mb={1.5}>
              <Grid item xs={6}><StatRow icon={<GroupsIcon fontSize="small" />} color="#3B82F6" label="Total Agents" value={fmtNum(ao.total_agents)} /></Grid>
              <Grid item xs={6}><StatRow icon={<GroupsIcon fontSize="small" />} color="#22C55E" label="Active Agents" value={fmtNum(ao.active_agents)} /></Grid>
              <Grid item xs={6}><StatRow icon={<WarningAmberIcon fontSize="small" />} color="#EF4444" label="Suspended" value={fmtNum(ao.suspended_agents)} /></Grid>
              <Grid item xs={6}><StatRow icon={<MonetizationOnIcon fontSize="small" />} color="#A855F7" label="Total Commission" value={fmtMoney(ao.total_commission)} /></Grid>
            </Grid>
            <Typography variant="caption" color="grey.500">Top Performing Agents</Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    {['Agent', 'Users', 'Turnover', 'Commission', 'Status'].map((h) => (
                      <TableCell key={h} sx={{ color: 'grey.500', borderColor: '#334155' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topAgents.map((a) => (
                    <TableRow key={a.code}>
                      <TableCell sx={{ color: 'white', borderColor: '#334155' }}>
                        {a.code}<br /><Typography variant="caption" color="grey.500">{a.name}</Typography>
                      </TableCell>
                      <TableCell sx={{ color: 'grey.300', borderColor: '#334155' }}>{fmtNum(a.users)}</TableCell>
                      <TableCell sx={{ color: 'grey.300', borderColor: '#334155' }}>{fmtMoney(a.turnover)}</TableCell>
                      <TableCell sx={{ color: 'grey.300', borderColor: '#334155' }}>{fmtMoney(a.commission)}</TableCell>
                      <TableCell sx={{ borderColor: '#334155' }}>
                        <Chip label={a.status} size="small"
                          sx={{ backgroundColor: a.status === 'active' ? '#22C55E22' : '#EF444422',
                            color: a.status === 'active' ? '#22C55E' : '#EF4444' }} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {topAgents.length === 0 && (
                    <TableRow><TableCell colSpan={5} sx={{ color: 'grey.500', borderColor: '#334155' }}>No agent-linked approved deposits yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Panel>
        </Grid>

        <Grid item xs={12} sm={6} lg={4}>
          <Panel title="Risk Alerts">
            {alerts.length === 0 && (
              <Typography variant="body2" color="grey.500">No active risk alerts</Typography>
            )}
            {alerts.map((a) => (
              <AlertRow key={a.title}
                icon={<WarningAmberIcon fontSize="small" />}
                color={a.severity === 'high' ? '#EF4444' : a.severity === 'medium' ? '#F59E0B' : '#3B82F6'}
                title={a.title} subtitle={a.subtitle} count={a.count} />
            ))}
          </Panel>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Panel title="Support Overview">
            <StatRow icon={<SupportAgentIcon fontSize="small" />} color="#3B82F6" label="Open Tickets" value={fmtNum(so.open_tickets)} />
            <StatRow icon={<WarningAmberIcon fontSize="small" />} color="#EF4444" label="High Priority" value={fmtNum(so.high_priority)} />
            <StatRow icon={<SupportAgentIcon fontSize="small" />} color="#F59E0B" label="Waiting for Agent" value={fmtNum(so.waiting_for_agent)} />
            <StatRow icon={<SupportAgentIcon fontSize="small" />} color="#22C55E" label="Resolved Today" value={fmtNum(so.resolved_today)} />
            <Box mt={2} display="flex" flexDirection="column" alignItems="center">
              <Typography variant="caption" color="grey.500" mb={0.5}>Avg. Response Time</Typography>
              <Typography variant="h6" color="#22C55E" fontWeight="bold">{so.avg_response_minutes} min</Typography>
            </Box>
          </Panel>
        </Grid>
      </Grid>

      {/* recent transactions + live activity (last 5 each) */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Panel title="Recent Transactions">
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    {['Type', 'User ID', 'Amount', 'Method', 'Status', 'Time'].map((h) => (
                      <TableCell key={h} sx={{ color: 'grey.500', borderColor: '#334155' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentTxns.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell sx={{ color: t.type === 'Withdrawal' ? '#EF4444' : '#22C55E', borderColor: '#334155' }}>
                        {t.type}
                      </TableCell>
                      <TableCell sx={{ color: 'grey.300', borderColor: '#334155' }}>{t.user_id}</TableCell>
                      <TableCell sx={{ color: 'white', borderColor: '#334155' }}>{fmtMoney(t.amount)}</TableCell>
                      <TableCell sx={{ color: 'grey.300', borderColor: '#334155' }}>{t.method}</TableCell>
                      <TableCell sx={{ borderColor: '#334155' }}>
                        <Chip label={t.status} size="small" sx={{
                          backgroundColor: t.status === 'Approved' ? '#22C55E22' : t.status === 'Pending' ? '#F59E0B22' : '#EF444422',
                          color: t.status === 'Approved' ? '#22C55E' : t.status === 'Pending' ? '#F59E0B' : '#EF4444',
                        }} />
                      </TableCell>
                      <TableCell sx={{ color: 'grey.500', borderColor: '#334155' }}>
                        {new Date(t.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </TableCell>
                    </TableRow>
                  ))}
                  {recentTxns.length === 0 && (
                    <TableRow><TableCell colSpan={6} sx={{ color: 'grey.500', borderColor: '#334155' }}>No transactions yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Panel>
        </Grid>

        <Grid item xs={12} md={5}>
          <Panel title="Live Activity">
            {recentActivity.map((a, i) => (
              <Box key={i} display="flex" alignItems="center" gap={1.5} py={1}
                borderBottom={i < recentActivity.length - 1 ? '1px solid #334155' : 'none'}>
                <Avatar sx={{ width: 28, height: 28, backgroundColor: '#3B82F622', flexShrink: 0 }}>
                  <OnlinePredictionIcon sx={{ fontSize: 14, color: '#3B82F6' }} />
                </Avatar>
                <Box flex={1} minWidth={0}>
                  <Typography variant="body2" color="grey.300">{a.message}</Typography>
                </Box>
                <Typography variant="caption" color="grey.500" flexShrink={0}>
                  {new Date(a.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
            ))}
            {recentActivity.length === 0 && (
              <Typography variant="body2" color="grey.500">No recent activity</Typography>
            )}
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardOverview;